import moment from 'moment';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import PropTypes from 'prop-types';
import {useState} from 'react';
import get from 'lodash/get';
import {SingleDatePicker} from 'react-dates';
import 'react-dates/lib/css/_datepicker.css';
import 'react-dates/initialize';
import '../scheduling/SelectADate.scss';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimeField } from '@mui/x-date-pickers/TimeField';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Divider from '@mui/material/Divider';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import IconClose from '@mui/icons-material/Close';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import useUser from '../../hooks/useUser';
import * as validations from '../../helpers/validations';
import {getStates} from '../../helpers/selectOptions';
import {isBetaUser} from '../../helpers/providers';
import {useUpdateSessionMutation} from '../../redux/services/sessionApi';
import {useGetSessionTypesQuery} from '../../redux/services/sessionTypeApi';
import AddressField from '../common/AddressField';
import CustomInputLabel from '../common/CustomInputLabel';
import CustomSelect from '../common/CustomSelect';
import CustomNumberField from '../common/CustomNumberField';
import CustomTextField from '../common/CustomTextField';
import FindCaseInput from '../cases/FindCaseInput';
import Grid from "@mui/material/Grid";

const getFields = ({sessionTypes, formValues}) => ([
  {
    label: 'Session Type',
    name: 'session_type_id',
    type: 'select',
    helperText: null,
    placeholder: null,
    required: true,
    validate: () => true,
    options: (sessionTypes || []).map(st => ({
      value: st.id,
      label: st.title,
    })),
    providerOnly: true,
    grid: {
      xs: 12,
      md: 6,
    },
  },
  {
    label: 'Category',
    name: 'category',
    type: 'select',
    helperText: null,
    placeholder: null,
    required: true,
    validate: () => true,
    options: [
      {value: 'restore', label: 'Restore'},
      {value: 'optimize', label: 'Optimize'},
    ],
    providerOnly: true,
    grid: {
      xs: 12,
      md: 6
    }
  },
  {
    label: 'Date',
    name: 'date',
    type: 'date',
    helperText: null,
    placeholder: null,
    required: true,
    validate: () => true,
    providerOnly: true,
    grid: {
      xs: 12,
      md: 6
    }
  },
  {
    label: 'Time',
    name: 'time',
    type: 'time',
    helperText: null,
    placeholder: null,
    required: true,
    validate: () => true,
    providerOnly: true,
    grid: {
      xs: 12,
      md: 6
    }
  },
  {
    label: 'Duration',
    name: 'duration',
    type: 'number',
    min: 0,
    max: 100,
    step: 1,
    maxLength: 3,
    helperText: null,
    placeholder: null,
    required: true,
    validate: () => true,
    providerOnly: true,
    grid: {
      xs: 12,
      md: 6
    }
  },
  {
    label: 'Case',
    name: 'case_id',
    type: 'caseSelect',
    required: true,
    helperText: null,
    placeholder: null,
    isBeta: true,
    grid: {
      xs: 12,
      md: 6,
    },
    validate: () => true,
  },
  {
    label: 'Address',
    name: 'address',
    type: 'address',
    required: true,
    helperText: null,
    placeholder: null,
    fullWidth: true,
    hidden: (s, sessionType) => ['VIRTUAL'].includes(sessionType?.location_type),
    grid: {
      xs: 12,
      md: 6,
    },
    validate: (val, formValues, sessionType) => (
      sessionType?.location_type !== 'VIRTUAL'
      ? !!val?.address_line_1 && !!val?.address_city && !!val?.address_state && !!val?.address_zip
      : true
    ),
  },
  {
    label: 'Instructions',
    name: 'instructions',
    type: 'textarea',
    multiline: true,
    rows: 4,
    helperText: null,
    placeholder: null,
    required: false,
    grid: {
      xs: 12,
      md: 6,
    },
    validate: () => true,
    // advancedOption: true,
  },
  {
    label: 'Session Meeting URL',
    name: 'session_meeting_url',
    type: 'text',
    required: true,
    helperText: null,
    placeholder: null,
    validate: (val, formValues, sessionType) => (
      sessionType?.location_type === 'VIRTUAL'
      ? !!val // must be a string
      : true
    ),
    hidden: (s, sessionType) => sessionType?.location_type !== 'VIRTUAL',
    grid: {
      xs: 12,
      md: 6,
    },
  },
]);

const handleDatePick = ({setDate, onChange}) => momentObj => {
  setDate(momentObj);
  if (!momentObj) return;
  onChange({target: {value: momentObj.format('MM/DD/yyyy')}});
};

const TimeInput = props => {
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <CustomInputLabel required={props?.required}>
        {props.label}
      </CustomInputLabel>
      <TimeField
        label={null}
        placeholder='12:00 AM'
        sx={{
          maxWidth: '100%',
          'input': { padding: '11px 14px' }
        }}
        defaultValue={moment(props.defaultValue)}
        onChange={momentObj => props.onChange({
          target: {value: momentObj.format('hh:mm a')},
        })}
      />
    </LocalizationProvider>
  );
};

const DatePicker = props => {
  const [date, setDate] = useState(
    props.defaultValue
    ? moment(props.defaultValue)
    : null
  );
  const [focused, setFocus] = useState(false);
  const {onChange} = props;
  return (
    <>
      <CustomInputLabel required={props?.required}>
        {props.label}
      </CustomInputLabel>
      <Box className='custom-date-picker'>
        <SingleDatePicker
          id={props.id}
          date={date}
          onDateChange={handleDatePick({setDate, onChange})}
          focused={!!focused}
          onFocusChange={({focused}) => setFocus(focused)}
          numberOfMonths={1}
          hideKeyboardShortcutsPanel={true}
          noBorder={true}
          isOutsideRange={() => false}
          minDate={moment().subtract(2, 'years')}
        />
      </Box>
    </>
  );
};

const FieldInput = props => {
  const [blurred, setBlurred] = useState(false);
  const childProps = {
    ...props,
    error: !props.validate(props.value, props?.formValues, props?.sessionType) && blurred,
    onBlur: () => setBlurred(true),
  };
  switch (props.type) {
    case 'text':
      return <CustomTextField fullWidth={true} {...childProps} />;
    case 'textarea':
      return <CustomTextField fullWidth={true} multiline={true} rows={childProps.rows} {...childProps} />;
    case 'number':
      return <CustomNumberField sx={{'input': {padding: '11px 14px'}}} fullWidth={true} {...childProps} />;
    case 'date':
      return <Stack direction='column'>
        <DatePicker {...childProps} />
      </Stack>;
    case 'time':
      return<Stack direction='column'>
        <TimeInput fullWidth={true} {...childProps} />
      </Stack>;
    case 'select':
      return <CustomSelect fullWidth={true} {...childProps} disableClearable />;
    case 'caseSelect':
      return (
        <FindCaseInput 
          {...childProps} 
          onSubmit={childProps.onChange} 
          allowEdit={true} 
          allowCreate={true}
          includeNonOwned={true}
        />
      );
    case 'address':
      const addressFields = [
        'address_line_1',
        'address_line_2', 
        'address_city', 
        'address_state', 
        'address_zip',
      ];
      return (
        props.addressDialogOpen
        ? <AddressField
            {...childProps}
            showLabel={true}
            onChange={address => props?.onChange({target: {value: address}})}
          />
        : <Box 
          sx={{bgcolor: 'white', borderRadius: '3px', padding: 2, border: '1px solid #e1e1e1'}}
        >
          <Typography color='gray' fontWeight='700'>
            Address{' '}
            {props.sessionType.location_type === 'HOME' && '(Patient Home)'}
            {props.sessionType.location_type === 'PARTNER' && '(Partner Location)'}
            {props.sessionType.location_type === 'OTHER_MOBILE' && '(Other Mobile Visit)'}
          </Typography>
          <Typography color='gray'>{props.formValues?.address_line_1}</Typography>
          <Typography color='gray'>{props.formValues?.address_line_2 && `${props?.formValues?.address_line_2}`}</Typography>
          <Typography color='gray'>
            {props.formValues?.address_city && `${props.formValues?.address_city}`}
            {props.formValues?.address_state && `, ${props.formValues?.address_state}`}
            {props.formValues?.address_zip && `, ${props.formValues?.address_zip}`}
          </Typography>
          <Stack direction='row' justifyContent='center' >
            <Button 
              size='small' 
              variant='text'
              onClick={() => props.setAddressDialogOpen(true)}
            >
              Edit
            </Button>
          </Stack>
      </Box>
        );
    default:
      return <div></div>;
  }
};

const parseFormValues = formValues => {
  let data = omit(formValues, 'date', 'time', 'duration');
  const dateStr = `${formValues.date} ${formValues.time}`;
  data.date = moment(dateStr, 'MM/DD/yyyy hh:mm a').toISOString();
  data.duration = parseInt(formValues.duration, 10);
  if (data.case_id) data.case_id = parseInt(data.case_id, 10);
  console.log('parse.data', data);
  return data;
};

const handleFieldUpdate = ({
  formValues, 
  setFormValues, 
  sessionTypes = [], 
  session,
  setAddressDialogOpen,
}) => fieldName => e => {
  let value;
  let values;
  if (fieldName === 'address') {
    value = get(e, 'target.value', {});
    values = {
      ...formValues,
      ...value,
    };
  } else {
    value = get(e, 'target.value', '');
    values = {
      ...formValues,
      [fieldName]: value,
    };
  }
  if (fieldName === 'session_type_id') {
    setAddressDialogOpen(false);
    const sessionType = sessionTypes.find(st => st.id === value) || null;
    if (sessionType) {
      if (sessionType.location_type === 'VIRTUAL') {
        values.address_line_1 = null;
        values.address_line_2 = null;
        values.address_city = null;
        values.address_zip = null;
      } else if (sessionType.location_type === 'HOME') {
        values.session_meeting_url = null;
        values.address_line_1 = session.patient.address_line_1 || '';
        values.address_line_2 = session.patient.address_line_2 || '';
        values.address_city = session.patient.address_city || '';
        values.address_state = session.patient.address_state || '';
        values.address_zip = session.patient.address_zip || '';
      } else if (sessionType.location_type === 'PARTNER') {
        values.session_meeting_url = null;
        values.address_line_1 = sessionType.address_line_1 || '';
        values.address_line_2 = sessionType.address_line_2 || '';
        values.address_city = sessionType.address_city || '';
        values.address_state = sessionType.address_state || '';
        values.address_zip = sessionType.address_zip || '';
      } else if (sessionType.location_type === 'OTHER_MOBILE') {
        values.session_meeting_url = null;
        values.address_line_1 = null;
        values.address_line_2 = null;
        values.address_city = null;
        values.address_state = null;
        values.address_zip = null;
      }
    }
  }
  setFormValues(values);
};

const validateFormValues = (formValues, sessionType) => {
  if (!formValues?.session_type_id) {
    return {isValid: false, error: new Error('Session Type is required')};
  }
  if (!formValues?.date) {
    return {isValid: false, error: new Error('Date is required')};
  }
  if (!formValues?.time) {
    return {isValid: false, error: new Error('Time is required')};
  }
  if (!formValues?.duration && formValues?.duration !== 0) {
    return {isValid: false, error: new Error('Duration is required')};
  }
  if (!formValues?.category) {
    return {isValid: false, error: new Error('Category is required')};
  }
  if (sessionType?.location_type !== 'VIRTUAL') {
    if (!formValues?.address_line_1) {
      return {isValid: false, error: new Error('Address Line 1 is required')};
    }
    if (!formValues?.address_city) {
      return {isValid: false, error: new Error('Address City is required')};
    }
    if (!formValues?.address_state) {
      return {isValid: false, error: new Error('Address State is required')};
    }
    if (!formValues?.address_zip) {
      return {isValid: false, error: new Error('Address Zip is required')};
    }
  } else {
    if (!formValues.session_meeting_url) {
      return {isValid: false, error: new Error('Session Meeting URL is required for virtual sessions')};
    }
  }
  return {isValid: true, error: null};
};

const handleSubmit = ({
  sessionId,
  updateSession,
  setLoading,
  setError,
  onSubmitted,
  onClose,
}) => async formValues => {
  setLoading(true);
  setError(null);
  const body = parseFormValues(formValues);
  const req = await updateSession({id: sessionId, body});
  setLoading(false);
  if (req.error) {
    return setError(new Error(get(req, 'error.data.errors[0]')));
  } else {
    setError(null);
    setLoading(false);
    onSubmitted();
    onClose();
  }
};

const SessionForm = props => {
  const {session: s, isPatient} = props;
  const [formValues, setFormValues] = useState({
    ...pick(s, 'category', 'duration', 'instructions'),
    session_type_id: s?.appointment_scheduled?.session_type?.id,
    date: moment(s.date).format('M/D/Y'),
    time: moment(s.date).format('hh:mm a'),
    address_line_1: get(s, 'appointment_scheduled.address_line_1'),
    address_line_2: get(s, 'appointment_scheduled.address_line_2'),
    address_city: get(s, 'appointment_scheduled.address_city'),
    address_state: get(s, 'appointment_scheduled.address_state'),
    address_zip: get(s, 'appointment_scheduled.address_zip'),
    session_meeting_url: s?.appointment_scheduled?.session_meeting_url,
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(null);
  const [checked, setChecked] = useState(true);
  const [collapseAdvancedOptions, setCollapseAdvancedOptions] = useState(true);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [updateSession] = useUpdateSessionMutation();
  const user = useUser();
  const sessionTypesQuery = useGetSessionTypesQuery(
    {providerId: s?.provider?.id},
    {skip: isPatient || !s?.provider?.id}
  );
  const sessionTypes = sessionTypesQuery?.data || [];
  const sessionType = sessionTypes.find(st => 
    st.id === (formValues?.session_type_id || s?.appointment_scheduled?.session_type?.id)
  );
  const onSubmit = handleSubmit({
    sessionId: s.id,
    updateSession,
    setLoading,
    setError,
    onClose: props.onClose,
    onSubmitted: props.onSubmitted,
  });
  if (sessionTypes.length === 0 || sessionTypesQuery.isLoading) return <></>;
  return (
    <Box sx={{width: '100%'}}>
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Typography variant='h2' padding={2}>
          Edit Session
        </Typography>
        {
          props.onClose
          ? <Box>
              <IconButton onClick={props.onClose}>
                <IconClose />
              </IconButton>
            </Box>
          : null
        }
      </Stack>
      <Divider/>
      {error && <Alert severity='error'>{error.message}</Alert>}
      {!s.appointment_scheduled &&
        <Alert severity='warning'>
          This session has no appointment and is not editable.
        </Alert>
      }
      {
        s ?
          <>
            <Grid container spacing={2} direction={{xs: 'row'}} padding={2}>
              {getFields({sessionTypes, formValues})
                .filter(f => f.isBeta ? isBetaUser(user) : true)
                .filter(f => !f.advancedOption)
                .map(f =>
                  (isPatient && f.providerOnly) 
                  || (f.hidden && f.hidden(s, sessionType)) 
                  ? null :
                    <Grid item xs={f.grid?.xs ?? 12} sm={f.grid?.sm} md={f.grid?.md}>
                      <FieldInput
                        patientId={s.patient.id}
                        defaultValue={
                          f.name === 'time' ? get(s, 'date')
                          : ( 
                            f.name === 'address'
                            ? pick(
                                formValues?.address_line_1 ? formValues : {},
                                'address_line_1', 'address_line_2', 'address_city', 'address_state', 'address_zip'
                              )
                            : get(s, f.name) || get(s, `appointment_scheduled.${f.name}`)
                          )
                        }
                        onChange={e => {
                          handleFieldUpdate({
                            formValues,
                            setFormValues,
                            sessionTypes,
                            session: s,
                            setAddressDialogOpen,
                          })(f.name)(e);
                        }}
                        formValues={formValues}
                        sessionType={sessionType}
                        setAddressDialogOpen={setAddressDialogOpen}
                        addressDialogOpen={addressDialogOpen}
                        {...f}
                      />
                    </Grid>
                  )
              }
              {
                isPatient
                ? null
                  : <Grid item xs={12}>
                    <FormGroup>
                      <FormControlLabel
                        label='send a notification email to the patient'
                        control={
                          <Checkbox
                            checked={checked}
                            onChange={e => setChecked(e.target.checked)}
                          />
                        }
                      />
                    </FormGroup>
                  </Grid>
              }
              {/* <Stack spacing={1}>
                <Button
                  onClick={() => setCollapseAdvancedOptions(!collapseAdvancedOptions)}
                >
                  {collapseAdvancedOptions ? 
                    <><ExpandMoreIcon/> Show More Options </> 
                    : <><ExpandLessIcon/>Hide Options</>
                }
                </Button>
                <Accordion
                  expanded={!collapseAdvancedOptions} 
                  onChange={props.isOpen ? props.onClose : props.onOpen}
                  style={{border: 'none', boxShadow: 'none', backgroundColor: 'none'}}
                >
                  <AccordionDetails>
                  {getFields()
                    .filter(f => f.advancedOption)
                    .map(f =>
                    isPatient && f.providerOnly ? null :
                      <Grid item xs={f.grid?.xs ?? 12} sm={f.grid?.sm} md={f.grid?.md}>
                        <FieldInput
                          defaultValue={
                            f.name === 'time'
                            ? get(s, 'date')
                            : get(s, f.name) || get(s, `appointment_scheduled.${f.name}`)
                          }
                          onChange={handleFieldUpdate({formValues,setFormValues})(f.name)}
                          formValues={formValues}
                          {...f}
                        />
                      </Grid>
                    )
                  }
                  </AccordionDetails>
                </Accordion>
              </Stack> */}
              <Grid item xs={12}>
                <Stack alignItems='center'>
                  <Box sx={{width: 'fit-content'}}>
                    <Button
                      variant='primaryGradient'
                      disabled={loading}
                      onClick={() => {
                        const {isValid, error} = validateFormValues(formValues, sessionType);
                        if (!isValid) {
                          console.log('invalidFormValues', formValues);
                          return setError(error);
                        }
                        onSubmit({...formValues, send_notification: checked})
                      }}
                    >
                      Save Changes
                    </Button>
                  </Box>
                </Stack>
              </Grid>
            </Grid>

          </>
        : null
      }
    </Box>
  );
};

SessionForm.defaultProps = {
  onSubmitted: () => (true),
  onClose: () => (true),
};

SessionForm.propTypes = {
  session: PropTypes.object,
  onClose: PropTypes.func,
  onSubmitted: PropTypes.func,
  isPatient: PropTypes.bool,
};

export default SessionForm;
